<script lang="ts">
  // components
  import UIRouterLink from "@shared/svelte/Components/UIRouterLink.svelte";
  import ProductImage from "@shared/svelte/Components/ProductImage.svelte";
  import FormatMoney from "@shared/svelte/Components/FormatMoney.svelte";
  import Reorder from "@retailer/svelte/OrderDetail/Reorder.svelte";

  import ProductContainerDetails from "@shared/filters/product_container_details.js";
  import { pluralize } from "@shared/svelte/Modules/pluralize";

  // stores
  import LayoutStore from "@shared/svelte/Stores/LayoutStore";
  import StateSettingsStore from "@shared/svelte/Stores/StateSettingsStore";
  import { get as getStoreValue } from "svelte/store";

  // types
  import type { CartProduct } from "@shared/types/ProductLine";
  import type { Distributor } from "@shared/types/Distributor";

  export let product: CartProduct;
  export let Product;
  export let distributor: Distributor;

  const stateSettings = getStoreValue(StateSettingsStore);
  const total = (): number => (product.original_total ? product.original_total : product.total);
  const requestedBadgeLabel = (product: CartProduct): string => {
    let quantityString = "";
    if (product.original_case_quantity) {
      quantityString += pluralize("case", product.original_case_quantity, true);
    }
    if (product.original_unit_quantity) {
      if (quantityString.length) quantityString += ", ";
      quantityString +=
        product.original_unit_quantity + " " + Product.pluralContainerType(product.container_type);
    }
    return "Requested " + quantityString;
  };
</script>

<style>
  :global.request-image {
    padding: 0.25rem;
  }
</style>

<div
  class={"craft bg-neutral-light fizz-inset-stretch-24 order-detail__order-requested-item"}
  data-testid={"order-requested-product-" + product.id}
>
  <div class="fizz-flex-row-8 fizz-align-content-start">
    <UIRouterLink
      stateName="layout.product_detail"
      params={{ productId: product.product_line.id }}
      style="min-width: 80px;"
    >
      <ProductImage
        product={{ cloudinary_image_url: product.product_line.cloudinary_image_url }}
        fizz={true}
        height={$LayoutStore.isDesktop ? 80 : 60}
        width={$LayoutStore.isDesktop ? 80 : 60}
        className="fizz-bordered fizz-radius-medium request-image fizz-surface"
      />
    </UIRouterLink>
    <div
      class="fizz-flex-row-responsive fizz-full-width fizz-space-between fizz-align-content-start"
    >
      <UIRouterLink
        stateName="layout.product_detail"
        classes="nounderline-hover fizz-text-inherit"
        params={{ productId: product.product_line.id }}
      >
        <p class="fizz-text-large no-translate">
          {product.product_line.name}
        </p>
        <div class="fizz-text-subdued" class:fizz-stack-8={!product.sku}>
          <span class="fizz-text-strong">Size:</span>
          {ProductContainerDetails()(product, null, null, true)}
        </div>
        {#if product.sku}
          <div class="fizz-text-small fizz-text-subdued fizz-stack-8">
            SKU: {product.sku}
          </div>
        {/if}
        <div class="fizz-badge fizz-stack-8">
          <span class="fizz-text-small">{requestedBadgeLabel(product)}</span>
        </div>
      </UIRouterLink>
      <div class="fizz-stack-8">
        <div class="fizz-styles fizz-text-small fizz-text-strong" data-testid="product-total">
          <span
            class:fizz-text-deal={product.discount_amount > 0}
            class:color-black={product.discount_amount === 0}
            class="price-string fizz-text-subdued"
          >
            <FormatMoney input={total()} />
          </span>
        </div>
      </div>
    </div>
  </div>
  {#if !stateSettings["uses_future_pricing_with_min_bottle"]}
    <div class="fizz-flex-row fizz-justify-end">
      <div
        class="fizz-full-width"
        class:fizz-half-width={$LayoutStore.isDesktop}
        class:reorder-container={$LayoutStore.isDesktop}
      >
        <Reorder {product} productLine={product.product_line} {distributor} />
      </div>
    </div>
  {/if}
</div>
